
import zipObject from 'lodash/zipObject';

const appointmentStatusColors = Object.freeze({
  'Confirmation Pending': '!bg-amber-100 !text-amber-700',
  'Confirmed': '!bg-emerald-100 !text-emerald-700',
  'Cancelled': '!bg-rose-100 !text-rose-700',
  'No Show': '!bg-orange-100 !text-orange-700',
  'Reschedule Requested': '!bg-indigo-100 !text-indigo-700',
  'No show - Charge applied': '!bg-orange-200 !text-orange-800',
  'Billing Sent': '!bg-cyan-100 !text-cyan-700',
  'Note Completed': '!bg-green-100 !text-green-700',
  'Note Finalized': '!bg-green-100 !text-green-700',
});

const miscColors = Object.freeze({
  amountTag: 'bg-gray-50 text-gray-600',
  customService: 'text-black bg-gray-100',
  terminal: 'bg-white text-gray-500',
  task: 'text-gray-700',
  eobPosted: 'bg-sky-100 text-sky-500 !border-sky-300',
  eobNotPosted: 'bg-white text-gray-500',
  Ready: '!bg-emerald-100 !text-emerald-700',
  InProgress: '!bg-stone-200 !text-stone-600',
  Failed: '!bg-red-100 !text-red-700',
  Deceased: '!bg-red-100 !text-red-700',
  common: 'bg-blue-100 !text-blue-500',
  Crossover: '!bg-orange-100 !text-orange-700',
  // Delivery methods
  Email: 'bg-sky-100 text-sky-700',
  Paper: 'bg-amber-100 text-amber-800',
  'Patient Portal': 'bg-green-100 text-green-700',
});

// Dynamic colors for appointment status (up to 100+), cycling deterministically by index
export const appointmentStatusColorClasses = Object.freeze([
  // Base set from existing map
  ...Object.values(appointmentStatusColors),
  // Extended palette (bg-100/text-700 for readability)
  '!bg-blue-100 !text-blue-700',
  '!bg-teal-100 !text-teal-700',
  '!bg-purple-100 !text-purple-700',
  '!bg-pink-100 !text-pink-700',
  '!bg-emerald-100 !text-emerald-700',
  '!bg-indigo-100 !text-indigo-700',
  '!bg-sky-100 !text-sky-700',
  '!bg-lime-100 !text-lime-700',
  '!bg-amber-100 !text-amber-700',
  '!bg-fuchsia-100 !text-fuchsia-700',
  '!bg-cyan-100 !text-cyan-700',
  '!bg-orange-100 !text-orange-700',
  '!bg-rose-100 !text-rose-700',
  '!bg-green-100 !text-green-700',
  '!bg-red-100 !text-red-700',
  // 200/800 variations for more slots
  '!bg-blue-200 !text-blue-800',
  '!bg-teal-200 !text-teal-800',
  '!bg-purple-200 !text-purple-800',
  '!bg-pink-200 !text-pink-800',
  '!bg-emerald-200 !text-emerald-800',
  '!bg-indigo-200 !text-indigo-800',
  '!bg-sky-200 !text-sky-800',
  '!bg-lime-200 !text-lime-800',
  '!bg-amber-200 !text-amber-800',
  '!bg-fuchsia-200 !text-fuchsia-800',
  '!bg-cyan-200 !text-cyan-800',
  '!bg-orange-200 !text-orange-800',
  '!bg-rose-200 !text-rose-800',
  '!bg-slate-200 !text-slate-800',
  '!bg-gray-200 !text-gray-800',
  '!bg-green-200 !text-green-800',
  '!bg-red-200 !text-red-800',
  // 300/900 variations
  '!bg-blue-300 !text-blue-900',
  '!bg-teal-300 !text-teal-900',
  '!bg-purple-300 !text-purple-900',
  '!bg-pink-300 !text-pink-900',
  '!bg-emerald-300 !text-emerald-900',
  '!bg-indigo-300 !text-indigo-900',
  '!bg-sky-300 !text-sky-900',
  '!bg-lime-300 !text-lime-900',
  '!bg-amber-300 !text-amber-900',
  '!bg-fuchsia-300 !text-fuchsia-900',
  '!bg-cyan-300 !text-cyan-900',
  '!bg-orange-300 !text-orange-900',
  '!bg-rose-300 !text-rose-900',
  '!bg-slate-300 !text-slate-900',
  '!bg-gray-300 !text-gray-900',
  '!bg-green-300 !text-green-900',
  '!bg-red-300 !text-red-900',
  // duplicate patterns to ensure >= 100 entries when combined with appointmentStatusColors
  '!bg-blue-50 !text-blue-700',
  '!bg-teal-50 !text-teal-700',
  '!bg-purple-50 !text-purple-700',
  '!bg-pink-50 !text-pink-700',
  '!bg-emerald-50 !text-emerald-700',
  '!bg-indigo-50 !text-indigo-700',
  '!bg-sky-50 !text-sky-700',
  '!bg-lime-50 !text-lime-700',
  '!bg-amber-50 !text-amber-700',
  '!bg-fuchsia-50 !text-fuchsia-700',
  '!bg-cyan-50 !text-cyan-700',
  '!bg-orange-50 !text-orange-700',
  '!bg-rose-50 !text-rose-700',
  '!bg-slate-50 !text-slate-700',
  '!bg-gray-50 !text-gray-700',
  '!bg-green-50 !text-green-700',
  '!bg-red-50 !text-red-700',
]);

export function assignAppointmentStatusColors(statuses: string[]): Record<string, string> {
  const classes = appointmentStatusColorClasses;
  return {
    ...zipObject(
      statuses,
      statuses.map((s, index) => classes[index % classes.length]),
    ),
  };
}

export function useColorsHashMap(): Record<string, string> {
  return {
    ...appointmentStatusColors,
    ...miscColors,
  };
}
